export const COMBINED_KEY = "__combined__";

export const getDiffPercentValue = (forValue, againstValue) => {
  const forParsed = parseInt(forValue, 10);
  const againstParsed = parseInt(againstValue, 10);
  if (
    !Number.isFinite(forParsed) ||
    !Number.isFinite(againstParsed) ||
    againstParsed === 0
  ) {
    return 0;
  }
  return (forParsed / againstParsed) * 100;
};

export const getPointsDiffValue = (pointsDiffValue) => {
  const parsed = parseInt(pointsDiffValue, 10);
  if (!Number.isFinite(parsed)) {
    return 0;
  }
  return parsed;
};

// The OPTA RL2 feed is parsed with explicitArray: false, so a competition that
// has split into pools comes back with table.comp.group as an array of groups
// rather than a single group object. Everything downstream (this view and the
// VIZ graphics) expects a single group, so we normalise here.
export function getGroups(data) {
  const raw = data?.table?.comp?.group;
  if (!raw) {
    return [];
  }
  const list = Array.isArray(raw) ? raw : [raw];
  return list.map((group, index) => ({
    index,
    key: String(group?.id ?? index),
    name:
      group?.name ||
      group?.group_name ||
      group?.round_name ||
      "Group " + (index + 1),
    teams: !group?.team
      ? []
      : Array.isArray(group?.team)
        ? group.team
        : [group.team],
    raw: group,
  }));
}

export function isSplit(data) {
  return getGroups(data)?.length > 1;
}

function getOffsets(groups) {
  let running = 0;
  return groups.map((group) => {
    const start = running;
    running += group?.teams?.length || 0;
    return start;
  });
}

function withGroups(data, groups) {
  const wasArray = Array.isArray(data?.table?.comp?.group);
  return {
    ...data,
    table: {
      ...data?.table,
      comp: {
        ...data?.table?.comp,
        group: wasArray ? groups : groups?.[0],
      },
    },
  };
}

// Rebuilds the data with each group's team array replaced by fn(teams, group, index),
// keeping the original single-object / array shape.
export function mapGroups(data, fn) {
  const groups = getGroups(data);
  if (!groups?.length) {
    return data;
  }
  return withGroups(
    data,
    groups.map((group, index) => ({
      ...group.raw,
      team: fn(group.teams, group, index),
    })),
  );
}

// Start of day ranks are matched group by group, falling back to every start of
// day team so a team that has moved pool still gets an arrow.
export function attachLastRanks(data, sodData) {
  const sodGroups = getGroups(sodData);
  if (!sodGroups?.length) {
    return data;
  }
  const allSodTeams = sodGroups.flatMap((group) => group.teams);
  return mapGroups(data, (teams, group, index) => {
    const match =
      sodGroups.find((sod) => sod.key === group.key) || sodGroups[index];
    const pool = match?.teams?.length ? match.teams : allSodTeams;
    return teams.map((team) => ({
      ...team,
      lastRank: pool?.find((t) => t?.id === team?.id)?.rank,
    }));
  });
}

function tagTeam({ team, groupIndex, rank, offset }) {
  const parsedLastRank = parseInt(team?.lastRank, 10);
  return {
    ...team,
    __group: groupIndex,
    rank,
    lastRank:
      offset && Number.isFinite(parsedLastRank)
        ? offset + parsedLastRank
        : team?.lastRank,
  };
}

// Returns the data with table.comp.group collapsed down to the single group the
// user has asked for. Combined keeps the feed's group order (pool 1 sits above
// pool 2) and renumbers the ranks 1..N.
export function selectGroup(data, groupKey) {
  const groups = getGroups(data);
  if (!groups?.length) {
    return data;
  }
  const offsets = getOffsets(groups);
  const index = groups.findIndex((group) => group.key === groupKey);
  const combined = groups.length === 1 || groupKey === COMBINED_KEY || index < 0;

  let teams;
  let selected;
  if (combined) {
    selected = groups[0].raw;
    teams = groups.flatMap((group, groupIndex) =>
      group.teams.map((team, teamIndex) =>
        tagTeam({
          team,
          groupIndex,
          rank:
            groups.length > 1
              ? offsets[groupIndex] + teamIndex + 1
              : team?.rank,
          offset: groups.length > 1 ? offsets[groupIndex] : 0,
        }),
      ),
    );
  } else {
    selected = groups[index].raw;
    teams = groups[index].teams.map((team) =>
      tagTeam({ team, groupIndex: index, rank: team?.rank, offset: 0 }),
    );
  }

  return {
    ...data,
    table: {
      ...data?.table,
      comp: {
        ...data?.table?.comp,
        group: { ...selected, team: teams },
      },
    },
  };
}

export function sortByRank(a, b) {
  if (parseInt(a?.rank) > parseInt(b?.rank)) {
    return 1;
  }
  if (parseInt(a?.rank) < parseInt(b?.rank)) {
    return -1;
  }
  return sortByPoints(a, b);
}

export function sortByPoints(a, b) {
  if (parseInt(a?.points) > parseInt(b?.points)) {
    return -1;
  }
  if (parseInt(a?.points) < parseInt(b?.points)) {
    return 1;
  }

  if (getPointsDiffValue(a?.pointsdiff) < getPointsDiffValue(b?.pointsdiff)) {
    return 1;
  }
  if (getPointsDiffValue(a?.pointsdiff) > getPointsDiffValue(b?.pointsdiff)) {
    return -1;
  }
  let diffPercent_a = getDiffPercentValue(a?.for, a?.against);
  let diffPercent_b = getDiffPercentValue(b?.for, b?.against);
  if (diffPercent_a < diffPercent_b) {
    return 1;
  }
  if (diffPercent_a > diffPercent_b) {
    return -1;
  }
  return 0;
}

function stripInternal(team) {
  const { __group, lastRank, ...rest } = team || {};
  return { ...rest };
}

// Merges rows edited in the selected view back into the full (possibly split)
// data so saving a single pool never wipes the other pools.
export function applyGroupEdits({ data, groupKey, teams }) {
  const groups = getGroups(data);
  if (!groups?.length) {
    return data;
  }
  const offsets = getOffsets(groups);
  const combined =
    groups.length > 1 &&
    (groupKey === COMBINED_KEY ||
      groups.findIndex((group) => group.key === groupKey) < 0);

  const next = groups.map((group) => ({
    ...group.raw,
    team: group.teams.map((team) => stripInternal(team)),
  }));

  (teams || []).forEach((row) => {
    let groupIndex =
      typeof row?.__group === "number"
        ? row.__group
        : groups.findIndex((group) =>
            group.teams.some((team) => team?.id === row?.id),
          );
    if (groupIndex < 0 || !next[groupIndex]) {
      return;
    }
    const clean = stripInternal(row);
    if (combined) {
      const parsedRank = parseInt(row?.rank, 10);
      if (Number.isFinite(parsedRank)) {
        clean.rank = (parsedRank - offsets[groupIndex]).toString();
      }
    }
    const teamIndex = next[groupIndex].team.findIndex(
      (team) => team?.id === row?.id,
    );
    if (teamIndex > -1) {
      next[groupIndex].team[teamIndex] = {
        ...next[groupIndex].team[teamIndex],
        ...clean,
      };
    }
  });

  next.forEach((group) => {
    group.team = group.team?.sort(sortByRank);
  });

  return withGroups(data, next);
}
